import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import * as Yup from 'yup';
import { DatePicker, EntitySelector, TimePicker } from '../../../shared/components';
import { Button } from '../../../shared/components/MUI/button/Button';
import { CreateClientModal } from '../../../shared/components/SconApp/createClientModal/CreateClientModal';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import {
  IBusinessView,
  IProductView,
  IServiceView,
  IClientView,
  ICreateAppointment,
  appointmentService,
  businessEmployeeService,
  clientService,
  serviceService,
  productService,
  feedback,
} from '../../../shared/services';
import { Api } from '../../../shared/services/api/axios-config';
import { setCsrfRequestToken } from '../../../shared/services/api/axios-config/interceptors';

interface IProps {
  business: IBusinessView;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const NewOrderModal: React.FC<IProps> = ({ business, open, onClose, onSuccess }) => {
  const [selectedClient, setSelectedClient] = useState<IClientView | null>(null);
  const [customer, setCustomer] = useState<string>();
  const [clientSearch, setClientSearch] = useState('');
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>();
  const [selectedServices, setSelectedServices] = useState<IServiceView[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<IProductView[]>([]);
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [time, setTime] = useState<Dayjs | null>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const schema = useMemo(() => Yup.object().shape({
    professionalId: Yup.number()
      .typeError('Selecione um profissional.')
      .required('Selecione um profissional.'),
    servicesOrProducts: Yup.mixed().test(
      'at-least-one',
      'Adicione pelo menos um serviço ou produto.',
      (_value, context) => {
        const { services, products } = context.parent;
        return services.length > 0 || products.length > 0;
      }
    ),
    date: Yup.mixed().required('Selecione data e horário.'),
    time: Yup.mixed().required('Selecione data e horário.'),
    client: Yup.mixed().required('Selecione ou informe um cliente.'),
  }), []);

  // Reset state when modal opens/closes
  const resetForm = useCallback(() => {
    setSelectedClient(null);
    setCustomer(undefined);
    setClientSearch('');
    setClientModalOpen(false);
    setSelectedEmployeeId(undefined);
    setSelectedServices([]);
    setSelectedProducts([]);
    setDate(dayjs());
    setTime(null);
    setFormErrors({});
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const { data: employees } = useQuery({
    queryKey: ['businessEmployees', business.id],
    queryFn: () => businessEmployeeService.get(business.id, true),
  });

  const { data: services } = useQuery({
    queryKey: ['businessServices', business.id],
    queryFn: () => serviceService.getByBusiness(business.id, true),
  });

  const { data: products } = useQuery({
    queryKey: ['businessProducts', business.id],
    queryFn: () => productService.getByBusiness(business.id, true),
  });

  const { data: clientResults, isFetching: isFetchingClients } = useQuery({
    queryKey: ['clientSearch', business.id, clientSearch],
    queryFn: () => clientService.searchClients(business.id, clientSearch || undefined),
    enabled: true,
  });

  const { mutate: createAppointment, isLoading } = useMutation(
    async (data: ICreateAppointment) => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      return appointmentService.create(data);
    },
    {
      onSuccess: () => {
        feedback('Comanda criada com sucesso!', 'success');
        resetForm();
        onSuccess();
        onClose();
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const selectedEmployee = useMemo(
    () => employees?.find(e => e.id === selectedEmployeeId),
    [employees, selectedEmployeeId],
  );

  // Service IDs that the selected employee offers
  const employeeServiceIds = useMemo(() => {
    if (!selectedEmployee?.businessEmployeeServices) return null;
    return new Set(selectedEmployee.businessEmployeeServices.map(bes => bes.serviceId));
  }, [selectedEmployee]);

  // Services filtered by the selected employee's assignments
  const availableServices = useMemo(() => {
    if (!services) return [];
    if (!employeeServiceIds) return services;
    return services.filter(s => employeeServiceIds.has(s.id));
  }, [services, employeeServiceIds]);

  const handleChangeEmployee = useCallback((id: number | undefined) => {
    setSelectedEmployeeId(id);
    // Clear services that the new employee doesn't offer
    if (id) {
      const emp = employees?.find(e => e.id === id);
      if (emp?.businessEmployeeServices) {
        const validIds = new Set(emp.businessEmployeeServices.map(bes => bes.serviceId));
        setSelectedServices(prev => prev.filter(s => validIds.has(s.id)));
      }
    } else {
      setSelectedServices([]);
    }
  }, [employees]);

  const handleSubmit = useCallback(() => {
    setFormErrors({});

    const validationData = {
      professionalId: selectedEmployee?.id,
      services: selectedServices,
      products: selectedProducts,
      date,
      time,
      client: selectedClient || customer,
    };

    try {
      schema.validateSync(validationData, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        setFormErrors(errors);
        const firstError = Object.values(errors)[0];
        if (firstError) feedback(firstError, 'error');
      }
      return;
    }

    const hour = time!.hour() ?? 0;
    const minute = time!.minute() ?? 0;

    const appointmentDate = date!.hour(hour).minute(minute).second(0).millisecond(0).toDate();

    const payload: ICreateAppointment = {
      date: appointmentDate,
      businessId: business.id,
      businessEmployeeId: selectedEmployee!.id,
      services: selectedServices.map(s => ({ serviceId: s.id })),
      products: selectedProducts.map(p => ({ productId: p.id })),
      isWalkIn: true,
      userId: selectedClient?.userId || undefined,
      customer: selectedClient
        ? (selectedClient.userId ? undefined : selectedClient.name)
        : customer || undefined,
    };

    createAppointment(payload);
  }, [selectedEmployee, selectedServices, selectedProducts, date, time, selectedClient, customer, business, createAppointment, schema]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth disableEscapeKeyDown={autocompleteOpen}>
      <DialogTitle>Nova Comanda</DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 1 }}>
          {/* Client — freeSolo + CreateClientModal */}
          <Box display='flex' gap={1} alignItems='flex-start'>
            <Autocomplete
              sx={{ flex: 1 }}
              options={clientResults || []}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.name}${opt.phone ? ` — ${opt.phone}` : ''}`}
              value={selectedClient}
              onChange={(_, value) => {
                setFormErrors(prev => ({ ...prev, client: '' }));
                if (value && typeof value !== 'string') {
                  setSelectedClient(value);
                  setCustomer('');
                } else {
                  setSelectedClient(null);
                  if (typeof value === 'string') {
                    setCustomer(value);
                  }
                }
              }}
              onInputChange={(_, value) => {
                setFormErrors(prev => ({ ...prev, client: '' }));
                setClientSearch(value);
                if (!selectedClient || (selectedClient && value !== selectedClient.name)) {
                  setCustomer(value);
                }
              }}
              freeSolo
              onOpen={() => setAutocompleteOpen(true)}
              onClose={() => setAutocompleteOpen(false)}
              loading={isFetchingClients}
              loadingText='Buscando clientes...'
              noOptionsText='Nenhum cliente encontrado'
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Cliente'
                  required
                  variant='outlined'
                  error={!!formErrors.client}
                  helperText={formErrors.client || ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetchingClients ? <LinearProgress sx={{ width: 20 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <IconButton
              onClick={() => setClientModalOpen(true)}
              color='primary'
              title='Novo cliente'
              sx={{ mt: 0.5 }}
            >
              <PersonAddIcon />
            </IconButton>
          </Box>

          {/* Professional */}
          <EntitySelector
            options={employees?.map(e => ({ id: e.id, name: e.name })) || []}
            value={selectedEmployeeId}
            onChange={handleChangeEmployee}
            label='Profissional *'
          />

          {/* Services (optional) — filtered by selected professional */}
          <Autocomplete
            multiple
            options={availableServices}
            getOptionLabel={(opt) => `${opt.description} — R$ ${opt.amount.toFixed(2)}`}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            value={selectedServices}
            onChange={(_, value) => setSelectedServices(value)}
            disabled={!selectedEmployee}
            noOptionsText={selectedEmployee ? 'Nenhum serviço disponível' : 'Selecione um profissional primeiro'}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => {
                const { key, ...tagProps } = getTagProps({ index: idx });
                return (
                  <Chip key={key} label={`${opt.description} — R$ ${opt.amount.toFixed(2)}`} size='small' {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField {...params} label='Serviços (opcional)' />
            )}
          />

          {/* Products */}
          <Autocomplete
            multiple
            options={products || []}
            getOptionLabel={(opt) => `${opt.description} — R$ ${opt.amount.toFixed(2)}`}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            value={selectedProducts}
            onChange={(_, value) => setSelectedProducts(value)}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => {
                const { key, ...tagProps } = getTagProps({ index: idx });
                return (
                  <Chip key={key} label={`${opt.description} — R$ ${opt.amount.toFixed(2)}`} size='small' {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField {...params} label='Produtos' />
            )}
          />

          {/* Date */}
          <DatePicker
            label='Data *'
            value={date}
            onChange={(value: Dayjs | null) => setDate(value)}
            size='small'
          />

          {/* Time */}
          <TimePicker
            label='Horário *'
            value={time}
            onChange={(value: Dayjs | null) => setTime(value)}
          />

          {/* Summary */}
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Typography variant='caption' color='text.secondary'>Resumo</Typography>
            <Typography variant='body2'>
              Profissional: {selectedEmployee?.name || '—'}
            </Typography>
            <Typography variant='body2'>
              Serviços: {selectedServices.length > 0
                ? `R$ ${selectedServices.reduce((t, s) => t + s.amount, 0).toFixed(2)}`
                : 'Nenhum'}
            </Typography>
            <Typography variant='body2'>
              Produtos: {selectedProducts.length > 0
                ? `R$ ${selectedProducts.reduce((t, p) => t + p.amount, 0).toFixed(2)}`
                : 'Nenhum'}
            </Typography>
          </Box>

          {/* Actions */}
          <Box display='flex' justifyContent='flex-end' gap={1} sx={{ mt: 1 }}>
            <Button label='Cancelar' variant='outlined' onClick={handleClose} />
            <Button
              label='Criar Comanda' data-testid='btn-create-order'
              variant='contained'
              onClick={handleSubmit}
              isLoading={isLoading}
            />
          </Box>
        </Box>
      </DialogContent>

      <CreateClientModal
        open={clientModalOpen}
        businessId={business.id}
        onClose={() => setClientModalOpen(false)}
      />
    </Dialog>
  );
};
